<template>
  <Teleport to="body">
    <div v-if="visible" class="atlas-help-overlay" @click.self="$emit('close')">
      <div class="atlas-help-modal">
        <div class="modal-header">
          <div class="header-title">
            <svg class="header-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
              <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span>What can Atlas help with?</span>
          </div>
          <button type="button" class="close-button" @click="$emit('close')">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>

        <div class="modal-tabs">
          <button
            type="button"
            class="tab-button"
            :class="{ active: activeTab === 'examples' }"
            @click="activeTab = 'examples'"
          >
            Example Questions
          </button>
          <button
            type="button"
            class="tab-button"
            :class="{ active: activeTab === 'glossary' }"
            @click="activeTab = 'glossary'"
          >
            Glossary
          </button>
        </div>

        <div class="modal-body">
          <!-- Examples Tab -->
          <div v-if="activeTab === 'examples'" class="examples-content">
            <div
              v-for="category in categories"
              :key="category.id"
              class="category-section"
            >
              <button
                type="button"
                class="category-header"
                @click="toggleCategory(category.id)"
              >
                <div class="category-title-row">
                  <span class="category-icon" :style="{ color: category.color }">
                    <component :is="getCategoryIcon(category.icon)" />
                  </span>
                  <span class="category-title">{{ category.title }}</span>
                </div>
                <svg
                  class="chevron"
                  :class="{ expanded: expandedCategories.includes(category.id) }"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <div class="category-examples" :class="{ expanded: expandedCategories.includes(category.id) }">
                <button
                  v-for="(example, idx) in category.examples"
                  :key="idx"
                  type="button"
                  class="example-item"
                  @click="selectExample(example.prompt)"
                >
                  <span class="example-text">{{ example.text }}</span>
                  <svg class="arrow-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <div class="data-note">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <span>{{ dataNote }}</span>
            </div>
          </div>

          <!-- Glossary Tab -->
          <div v-if="activeTab === 'glossary'" class="glossary-content">
            <div class="glossary-grid">
              <div
                v-for="(item, key) in glossary"
                :key="key"
                class="glossary-item"
              >
                <div class="glossary-term">{{ item.term }}</div>
                <div class="glossary-definition">{{ item.definition }}</div>
                <div class="glossary-category">{{ item.category }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { ATLAS_HELP_CATEGORIES, ATLAS_GLOSSARY, ATLAS_DATA_NOTE } from '@/data/atlasHelpData.js';

// Icon components for each category
const HeartbeatIcon = {
  template: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 12H18L15 21L9 3L6 12H2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`
};

const ChartLineIcon = {
  template: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 3V21H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M7 14L12 9L16 13L21 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`
};

const BugIcon = {
  template: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 9V7C9 5.34315 10.3431 4 12 4C13.6569 4 15 5.34315 15 7V9" stroke="currentColor" stroke-width="2"/>
    <rect x="6" y="9" width="12" height="11" rx="4" stroke="currentColor" stroke-width="2"/>
    <path d="M6 13H2M22 13H18M6 17H2M22 17H18M8 9L5 6M16 9L19 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  </svg>`
};

const UsersIcon = {
  template: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="7" r="3" stroke="currentColor" stroke-width="2"/>
    <path d="M3 20C3 16.6863 5.68629 14 9 14C12.3137 14 15 16.6863 15 20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    <circle cx="17" cy="8" r="2.5" stroke="currentColor" stroke-width="2"/>
    <path d="M16 14C18.7614 14 21 16.2386 21 19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  </svg>`
};

const MapMarkerIcon = {
  template: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21C12 21 19 15.0002 19 10C19 6.13401 15.866 3 12 3C8.13401 3 5 6.13401 5 10C5 15.0002 12 21 12 21Z" stroke="currentColor" stroke-width="2"/>
    <circle cx="12" cy="10" r="2" stroke="currentColor" stroke-width="2"/>
  </svg>`
};

const FlaskIcon = {
  template: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 3H15M10 3V8.5L4 18C3.5 19 4.2 20 5.5 20H18.5C19.8 20 20.5 19 20 18L14 8.5V3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`
};

const GraduationCapIcon = {
  template: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 4L2 9L12 14L22 9L12 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M6 11.5V16.5C6 16.5 8 19 12 19C16 19 18 16.5 18 16.5V11.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`
};

export default {
  name: 'AtlasHelpModal',
  components: {
    HeartbeatIcon,
    ChartLineIcon,
    BugIcon,
    UsersIcon,
    MapMarkerIcon,
    FlaskIcon,
    GraduationCapIcon,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'select-prompt'],
  data() {
    return {
      activeTab: 'examples',
      expandedCategories: ['health', 'performance'], // Start with first two expanded
      categories: ATLAS_HELP_CATEGORIES,
      glossary: ATLAS_GLOSSARY,
      dataNote: ATLAS_DATA_NOTE,
    };
  },
  methods: {
    getCategoryIcon(iconName) {
      const iconMap = {
        'heartbeat': 'HeartbeatIcon',
        'chart-line': 'ChartLineIcon',
        'bug': 'BugIcon',
        'users': 'UsersIcon',
        'map-marker': 'MapMarkerIcon',
        'flask': 'FlaskIcon',
        'graduation-cap': 'GraduationCapIcon',
      };
      return iconMap[iconName] || 'HeartbeatIcon';
    },
    toggleCategory(categoryId) {
      const idx = this.expandedCategories.indexOf(categoryId);
      if (idx > -1) {
        this.expandedCategories.splice(idx, 1);
      } else {
        this.expandedCategories.push(categoryId);
      }
    },
    selectExample(prompt) {
      this.$emit('select-prompt', prompt);
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.atlas-help-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.atlas-help-modal {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
}

.header-icon {
  color: #052f49;
}

.close-button {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.close-button:hover {
  background: #f3f4f6;
  color: #111827;
}

.modal-tabs {
  display: flex;
  gap: 0.25rem;
  padding: 0.75rem 1.5rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.tab-button {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
}

.tab-button:hover {
  color: #374151;
  background: #e5e7eb;
}

.tab-button.active {
  color: #052f49;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem 1.5rem;
}

/* Examples Content */
.examples-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.category-section {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.category-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem;
  background: #f9fafb;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
}

.category-header:hover {
  background: #f3f4f6;
}

.category-title-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.category-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.category-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.chevron {
  color: #9ca3af;
  transition: transform 0.2s;
}

.chevron.expanded {
  transform: rotate(180deg);
}

.category-examples {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.2s ease-out;
}

.category-examples.expanded {
  grid-template-rows: 1fr;
}

.category-examples > * {
  overflow: hidden;
}

.example-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.75rem 1rem;
  background: white;
  border: none;
  border-top: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background 0.15s;
  text-align: left;
}

.example-item:hover {
  background: #f0fdf9;
}

.example-text {
  font-size: 0.8125rem;
  color: #4b5563;
}

.arrow-icon {
  color: #9ca3af;
  opacity: 0;
  transition: opacity 0.15s;
}

.example-item:hover .arrow-icon {
  opacity: 1;
  color: #08c1c8;
}

.data-note {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  font-size: 0.75rem;
  color: #0369a1;
  margin-top: 1rem;
}

.data-note svg {
  flex-shrink: 0;
  margin-top: 0.0625rem;
}

/* Glossary Content */
.glossary-content {
  padding-top: 0.5rem;
}

.glossary-grid {
  display: grid;
  gap: 0.75rem;
}

.glossary-item {
  padding: 0.875rem 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.glossary-term {
  font-size: 0.875rem;
  font-weight: 600;
  color: #052f49;
  margin-bottom: 0.25rem;
}

.glossary-definition {
  font-size: 0.8125rem;
  color: #4b5563;
  line-height: 1.4;
}

.glossary-category {
  font-size: 0.6875rem;
  color: #9ca3af;
  margin-top: 0.375rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}
</style>
